import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { logout, supabase } from '../lib/supabase';
import { LayoutDashboard, PlusCircle, LogOut, Sun, Moon, UserCircle, Inbox, Sparkles } from 'lucide-react';
import { useTheme } from '../lib/ThemeContext';
import AuthModal from './AuthModal';

interface NavbarProps { user: User | null; }

export default function Navbar({ user }: NavbarProps) {
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch pending retake inbox count
  useEffect(() => {
    if (!user) return;
    const fetchInboxCount = async () => {
      const { data: tests } = await supabase
        .from('tests')
        .select('id')
        .eq('creator_id', user.id);
      if (!tests?.length) return;
      const testIds = tests.map(t => t.id);
      const { count } = await supabase
        .from('retake_notifications')
        .select('id', { count: 'exact', head: true })
        .in('test_id', testIds)
        .eq('status', 'pending');
      setInboxCount(count ?? 0);
    };
    fetchInboxCount();
  }, [user, pathname]);

  if (pathname === '/dashboard') return null;
  // Recipients answering an anonymous session should land on nothing but
  // the question card — no branding chrome, no Sign In prompt.
  if (pathname.startsWith('/test/')) return null;

  const initial = user ? (user.user_metadata?.full_name || user.email || 'U')[0].toUpperCase() : 'U';
  const displayName = user ? (user.user_metadata?.full_name || user.email?.split('@')[0] || 'User') : '';

  return (
    <>
    <nav className="border-b border-brand-border bg-brand-bg/90 backdrop-blur-xl sticky top-0 z-50">
      <div className="px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-brand-accent rounded flex items-center justify-center">
            <span className="font-bold text-white text-base leading-none">Y</span>
          </div>
          <span className="text-base font-bold tracking-widest uppercase" style={{ color: 'var(--color-brand-text)' }}>
            Yhuu
          </span>
        </Link>

        <div className="flex items-center gap-3">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="w-8 h-8 rounded-full border border-brand-border flex items-center justify-center transition-colors hover:border-brand-accent cursor-pointer"
            style={{ background: 'var(--color-brand-card)' }}
          >
            {theme === 'dark'
              ? <Sun className="w-4 h-4 text-zinc-400" />
              : <Moon className="w-4 h-4" style={{ color: 'var(--color-brand-accent)' }} />
            }
          </button>

          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="text-zinc-400 hover:text-brand-accent transition-colors">
                <LayoutDashboard className="w-5 h-5" />
              </Link>
              <Link to="/create" className="text-zinc-400 hover:text-brand-accent transition-colors">
                <PlusCircle className="w-5 h-5" />
              </Link>
              <Link to="/inbox" className="relative text-zinc-400 hover:text-brand-accent transition-colors">
                <Inbox className="w-5 h-5" />
                {inboxCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-brand-accent text-white text-[9px] font-black flex items-center justify-center leading-none">
                    {inboxCount > 9 ? '9+' : inboxCount}
                  </span>
                )}
              </Link>

              {/* Avatar + dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setOpen(v => !v)}
                  className="w-8 h-8 rounded-full bg-brand-accent flex items-center justify-center text-white text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity border-2 border-brand-accent"
                >
                  {initial}
                </button>

                {open && (
                  <div
                    className="absolute right-0 mt-2 w-52 rounded-2xl border border-brand-border shadow-xl overflow-hidden z-50"
                    style={{ background: 'var(--color-brand-card)' }}
                  >
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-brand-border">
                      <p className="text-xs font-bold truncate" style={{ color: 'var(--color-brand-text)' }}>{displayName}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{user.email}</p>
                    </div>

                    {/* Menu items */}
                    <Link
                      to="/profile"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-400 hover:text-brand-accent hover:bg-brand-accent/5 transition-colors"
                    >
                      <UserCircle className="w-4 h-4" /> Profile & Settings
                    </Link>
                    <Link
                      to="/dashboard"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-400 hover:text-brand-accent hover:bg-brand-accent/5 transition-colors"
                    >
                      <LayoutDashboard className="w-4 h-4" /> My Sessions
                    </Link>
                    <Link
                      to="/create?liveAi=1"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-400 hover:text-brand-accent hover:bg-brand-accent/5 transition-colors"
                    >
                      <Sparkles className="w-4 h-4" /> Live Yhuu AI
                    </Link>

                    <div className="border-t border-brand-border">
                      <button
                        onClick={async () => { setOpen(false); await logout(); navigate('/'); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-brand-accent hover:bg-brand-accent/5 transition-colors cursor-pointer"
                      >
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAuthModal(true)} className="px-4 py-2 bg-brand-accent text-white rounded-lg text-xs font-bold cursor-pointer">
              Sign In
            </button>
          )}
        </div>
      </div>
    </nav>
    {/* Rendered as a sibling of <nav>, not a child — <nav> has
        backdrop-blur-xl, and backdrop-filter (like transform) creates a new
        containing block for position:fixed descendants, which was trapping
        this modal inside the navbar's own box instead of covering the
        screen. */}
    {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} defaultMode="login" />}
    </>
  );
}
